//./services/OutboundService.ts
import { GlobalBus } from '@core/Bus/EventBus';
import { IAdaptor } from '@Web/adapters/IAdaptor';
import { AuthService } from '@Web/services/AuthService';
import { getAuthToken } from '@core/utils/authToken';

export class OutboundService {
  private adaptor: IAdaptor;
  private authService: AuthService;

  constructor(adaptor: IAdaptor) {
    this.adaptor = adaptor;
    this.authService = AuthService.getInstance();
  }

  /**
   * NEW: Public getter to allow controlled access to the adaptor instance
   * from the controller, for tasks like forcing a disconnect.
   */
  public getAdaptor(): IAdaptor {
    return this.adaptor;
  }

  async connect(): Promise<void> {
    if (this.adaptor.getConnected()) {
      console.log('Connection already active. Skipping.');
      return;
    }

    try {
      // First check for auth token in cookies (set by gaming-portal)
      // Fall back to AuthService token if no cookie token is found
      const cookieToken = getAuthToken();
      let token = cookieToken;
      const tokenSource = cookieToken ? 'cookie' : 'AuthService';

      if (!token) {
        token = this.authService.getAccessToken();
      }

      if (!token) {
        throw new Error('No access token available for STOMP connection.');
      }

      console.log(
        '[OutboundService] Connecting to STOMP with token from',
        tokenSource
      );

      await this.adaptor.connect({
        queryParams: { token: token },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (err) {
      console.error('Failed to establish connection:', err);
      const message = err instanceof Error ? err.message : String(err);
      // Handshake/network failures are retried by StompAdaptor. Only give up
      // here when we cannot even attempt a useful reconnect.
      if (message.includes('No access token')) {
        GlobalBus.emit('socketio:status', 'failed');
      }
      throw err;
    }
  }

  disconnect(): void {
    this.adaptor.disconnect();
  }
  stopAutoReconnect(): void {
    this.adaptor.stopAutoReconnect?.();
  }
  getConnectionGeneration(): number {
    return this.adaptor.getConnectionGeneration?.() ?? 0;
  }

  async reconnect(): Promise<void> {
    if (this.adaptor.isWaitingForNetwork?.()) {
      return;
    }
    const cookieToken = getAuthToken();
    const token = cookieToken || this.authService.getAccessToken();
    if (!token) {
      throw new Error('No access token available for STOMP reconnect.');
    }
    const options = {
      queryParams: { token },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    if (this.adaptor.hardReconnect) {
      await this.adaptor.hardReconnect(options);
      return;
    }
    this.adaptor.forceDisconnect?.();
  }

  on(event: string, handler: (payload: any) => void): () => void {
    return this.adaptor.on(event, handler);
  }

  emit(
    event: string,
    payload: any,
    _options?: { requireAck?: boolean; timeoutMs?: number }
  ): Promise<null> {
    this.adaptor.emit(event, payload);
    return Promise.resolve(null);
  }
}
